<template lang="pug">
li.list-item(
	:class="[{ clickable: hasClick, pills: hasPills, 'nav-button': hasViewButton }]"
)
	component(
		:is="navigationLink ? 'router-link' : 'div'",
		:to="navigationLink",
		:class="['content', item.lifecycleState]"
	)
		VIcon(v-if="icon", :name="icon")
		.text
			slot
		.lifecycle(v-if="item.lifecycleState")
			VTooltip(:text="stateTooltip", :position="tooltipPosition")
				VIcon(:name="stateIcon")
		.pills(v-if="hasPills")
			slot(name="pills")
	router-link.button.small.secondary(
		v-if="hasViewButton",
		:to="navigationLink"
	) View
</template>

<script>
import validate, { joi } from "@pv";

export default {
	name: "SimpleListItem",
	props: {
		item: {
			type: Object,
			required: false,
			validator: validate(
				joi.object({
					lifecycleState: joi.lifecycleState().allow("")
				})
			),
			default: () => ({ lifecycleState: "" })
		},
		icon: {
			type: String,
			required: false,
			default: ""
		},
		hasViewButton: {
			type: Boolean,
			required: false,
			default: false
		},
		tooltipPosition: {
			type: String,
			required: false,
			default: "top"
		},
		navigationLink: {
			type: String,
			required: false,
			default: null
		}
	},
	computed: {
		hasClick() {
			return !!this.$attrs.onClick;
		},
		hasPills() {
			return !!this.$slots.pills;
		},
		stateTooltip() {
			return this.$lifecycle.tooltip(this.item.lifecycleState);
		},
		stateIcon() {
			const state = this.item.lifecycleState;
			return this.$lifecycle.icon(state);
		}
	}
};
</script>

<style lang="scss" scoped>
.list-item {
	--list-item-padding-y: calc(
		(var(--standard-padding-height) + var(--standard-gap-height)) / 3 +
			#{rem-calc(1)}
	);
	--list-item-content-padding-y: var(--list-item-padding-y);
	--list-item-pill-gap: calc(var(--standard-gap-width) - #{rem-calc(2)});
	--list-item-nav-gap: var(--standard-gap-width);

	list-style: none;
	padding: var(--list-item-padding-y) 0;

	.collapsible-content & {
		&:first-of-type {
			padding-top: rem-calc(0);
		}

		&:last-of-type {
			padding-bottom: rem-calc(0);
		}
	}

	& + .list-item {
		border-top: 1px solid hsl(var(--white10));
	}

	&.pills {
		.content {
			.text {
				order: 1;
				flex-grow: 0;
			}

			.pills {
				order: 2;
				margin-left: var(--list-item-pill-gap);
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				gap: var(--list-item-pill-gap);

				#{v-deep('.pill')} {
					margin: 0;
				}
			}

			.lifecycle {
				order: 3;
				margin-left: auto;
			}
		}
	}

	&.nav-button {
		display: flex;
		justify-content: space-between;
		align-items: center;

		.content {
			flex-flow: column;
			align-items: flex-start;
			flex-grow: 1;
			margin-right: var(--list-item-nav-gap);

			.pills {
				margin: var(--list-item-nav-gap) 0 0;
			}
		}
	}

	.content {
		display: flex;
		align-items: center;
		font-size: var(--font-small-title-size);
		font-weight: var(--font-bolder);
		padding: var(--list-item-content-padding-y) 0;
		color: inherit;
		text-decoration: none;

		&.deprecated {
			.icon,
			.text,
			.pills {
				opacity: var(--deprecated-opacity);
			}
		}

		.text {
			flex-grow: 1;
		}
	}

	.icon,
	.small-icon {
		margin-right: var(--list-item-pill-gap);
	}

	> .button {
		margin: 0;
	}

	&.clickable {
		cursor: pointer;
		user-select: none;

		&:hover .content {
			opacity: 0.75;
		}
	}
}
</style>
